import {Component, DestroyRef, inject, OnDestroy} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormControl, ReactiveFormsModule, Validators} from '@angular/forms';
import {finalize, switchMap} from 'rxjs';
import {NzAlertComponent} from 'ng-zorro-antd/alert';
import {NzButtonModule} from 'ng-zorro-antd/button';
import {NzFormControlComponent, NzFormItemComponent, NzFormLabelComponent} from 'ng-zorro-antd/form';
import {NzInputDirective} from 'ng-zorro-antd/input';
import {NzStepsModule} from 'ng-zorro-antd/steps';
import {NZ_MODAL_DATA, NzModalRef} from 'ng-zorro-antd/modal';
import {NzNotificationService} from 'ng-zorro-antd/notification';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {EMAIL_VALIDATORS} from '../../../shared/email-validator';
import {AuthFlowService} from '../../../core/services/auth-flow.service';

export interface ForgotPasswordModalData {
  xml: string;
}

@Component({
  selector: 'app-forgot-password-modal',
  imports: [
    ReactiveFormsModule,
    NzAlertComponent,
    NzButtonModule,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzFormControlComponent,
    NzInputDirective,
    NzStepsModule,
    TranslatePipe,
  ],
  templateUrl: './forgot-password-modal.html',
})
export class ForgotPasswordModal implements OnDestroy {
  private readonly _data = inject<ForgotPasswordModalData>(NZ_MODAL_DATA);
  private readonly _modalRef = inject(NzModalRef);
  private readonly _authFlow = inject(AuthFlowService);
  private readonly _notification = inject(NzNotificationService);
  private readonly _translate = inject(TranslateService);
  private readonly _destroyRef = inject(DestroyRef);

  readonly emailControl = new FormControl('', {nonNullable: true, validators: EMAIL_VALIDATORS});
  readonly codeControl = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, Validators.pattern(/^\d{6}$/)],
  });

  currentStep = 0;
  isSendingCode = false;
  codeSent = false;
  resendCountdown = 0;
  isConfirmingCode = false;

  private countdownTimer?: ReturnType<typeof setInterval>;

  ngOnDestroy(): void {
    this.clearCountdown();
  }

  changeEmail(): void {
    this.currentStep = 0;
    this.codeSent = false;
    this.codeControl.reset('');
    this.clearCountdown();
    this.emailControl.enable({emitEvent: false});
    this._modalRef.updateConfig({nzOkDisabled: true});
  }

  sendCode(): void {
    if (this.emailControl.invalid || this.resendCountdown > 0) {
      this.emailControl.markAsTouched();
      return;
    }

    const email = this.emailControl.value;
    this.isSendingCode = true;

    this._authFlow.sendEmailCode({email})
      .pipe(finalize(() => this.isSendingCode = false), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this.codeSent = true;
          this.emailControl.disable({emitEvent: false});
          this.startCountdown(120);
          this._notification.success(
            this._translate.instant('forgotPsd.codeSentTitle'),
            this._translate.instant('forgotPsd.codeSentDesc')
          );
        },
        error: () => {},
      });
  }

  confirmCode(): void {
    if (this.codeControl.invalid || this.isConfirmingCode || this.currentStep === 1) {
      return;
    }

    const email = this.emailControl.value;
    this.isConfirmingCode = true;

    this._authFlow.confirmEmailCode({email, code: this.codeControl.value})
      .pipe(
        switchMap(() => this._authFlow.setEmailByXml({xml: this._data.xml, email})),
        finalize(() => this.isConfirmingCode = false),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: () => {
          this.currentStep = 1;
          this.clearCountdown();
          this.resendCountdown = 0;
          this.emailControl.disable({emitEvent: false});
          this._modalRef.updateConfig({nzOkDisabled: false});
          this._notification.success(
            this._translate.instant('forgotPsd.emailSavedTitle'),
            this._translate.instant('forgotPsd.emailSavedDesc')
          );
        },
        error: () => {},
      });
  }

  handleOk(): void {
    if (this.currentStep !== 1) {
      this._notification.warning(
        this._translate.instant('forgotPsd.confirmEmailFirstTitle'),
        this._translate.instant('forgotPsd.confirmEmailFirstDesc')
      );
      return;
    }

    this._modalRef.updateConfig({nzOkLoading: true, nzCancelDisabled: true});

    this._authFlow.forgotPsd({xml: this._data.xml})
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this._modalRef.close(true);
          this._notification.success(
            this._translate.instant('forgotPsd.linkSentTitle'),
            this._translate.instant('forgotPsd.linkSentDesc')
          );
        },
        error: () => {
          this._modalRef.updateConfig({nzOkLoading: false, nzCancelDisabled: false});
        }
      });
  }

  private startCountdown(seconds: number): void {
    this.clearCountdown();
    this.resendCountdown = seconds;
    this.countdownTimer = setInterval(() => {
      this.resendCountdown--;
      if (this.resendCountdown <= 0) {
        this.clearCountdown();
      }
    }, 1000);
  }

  private clearCountdown(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = undefined;
    }
  }
}
